import { useState } from "react";
import { Navigate } from "react-router-dom";
import { Plus, Building2, Pencil } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useFetch } from "@/hooks/useFetch";
import { api, errMsg } from "@/lib/api";
import { PageHeader, Loading, ErrorState } from "@/components/common/States";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { TextField } from "@/components/common/Fields";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";

export default function SitesPage() {
  const { user, refreshUser } = useAuth();
  const { data, loading, error, reload } = useFetch(() => api.get("/sites").then((r) => r.data), []);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: "", address: "", active: true });
  if (user?.role !== "admin") return <Navigate to="/" replace />;
  const open = (s) => { setEditing(s || {}); setForm(s ? { name: s.name, address: s.address || "", active: s.active !== false } : { name: "", address: "", active: true }); };
  const submit = async (e) => {
    e.preventDefault();
    try { editing.id ? await api.put(`/sites/${editing.id}`, form) : await api.post("/sites", form); toast.success("Sede salvata"); setEditing(null); reload(true); refreshUser(); }
    catch (err) { toast.error(errMsg(err)); }
  };
  return (
    <div className="fade-up" data-testid="sites-page">
      <PageHeader title="Sedi" subtitle="Ogni sede ha ricette, articoli, scorte e produzione propri" actions={<Button onClick={() => open(null)} data-testid="new-site-button"><Plus className="h-4 w-4" /> Nuova sede</Button>} />
      {loading ? <Loading /> : error ? <ErrorState message={error} onRetry={reload} /> : (
        <ul className="grid gap-3 sm:grid-cols-2">
          {data.map((s) => (
            <li key={s.id} className="card flex items-center gap-3 p-4" data-testid={`site-card-${s.id}`}>
              <span className="flex h-10 w-10 items-center justify-center rounded-md bg-accent text-primary"><Building2 className="h-5 w-5" /></span>
              <div className="min-w-0 flex-1"><div className="font-semibold">{s.name}</div><div className="text-xs text-slate-500">{s.address || "Indirizzo non indicato"}</div></div>
              <span className={`badge ${s.active !== false ? "badge-green" : "badge-grey"}`}>{s.active !== false ? "Attiva" : "Inattiva"}</span>
              <Button variant="ghost" size="icon" onClick={() => open(s)} aria-label="Modifica" data-testid={`edit-site-${s.id}`}><Pencil className="h-4 w-4" /></Button>
            </li>
          ))}
        </ul>
      )}
      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent data-testid="site-dialog"><DialogHeader><DialogTitle>{editing?.id ? "Modifica sede" : "Nuova sede"}</DialogTitle></DialogHeader>
          <form onSubmit={submit} className="space-y-3">
            <TextField label="Nome" value={form.name} onChange={(v) => setForm((f) => ({ ...f, name: v }))} required testId="site-name-input" />
            <TextField label="Indirizzo" value={form.address} onChange={(v) => setForm((f) => ({ ...f, address: v }))} testId="site-address-input" />
            <label className="flex items-center gap-2 text-sm"><Switch checked={form.active} onCheckedChange={(v) => setForm((f) => ({ ...f, active: v }))} data-testid="site-active-switch" /> Sede attiva</label>
            <Button type="submit" className="w-full" data-testid="site-submit">Salva</Button>
          </form></DialogContent>
      </Dialog>
    </div>
  );
}
